import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCreditUsageLogs } from '../../../../Requests/CreditUsageRequests';
import { getPurchaseEntries } from '../../../../Requests/PurchaseEntriesRequests';
import { getTeamUuid } from '../../../../utils/cookies';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import { Card } from '@tremor/react';
import { 
  CpuChipIcon, 
  CalendarIcon,
  UserIcon,
  CogIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

function BillingUsage() {
  const { t } = useTranslation();
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState('usage');
  const [logs, setLogs] = useState([]);
  const [purchaseEntries, setPurchaseEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    team_uuid: getTeamUuid(),
    page: 1,
    per_page: 15
  });


  useEffect(() => {
    fetchData();
  }, [filters, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if team_uuid exists
      const teamUuid = getTeamUuid();
      if (!teamUuid) {
        throw new Error('No team UUID found. Please ensure you are logged in and have access to a team.');
      }

      if (activeTab === 'usage') {
        // Filter out empty values to avoid sending empty strings as dates
        const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== '' && value !== null) {
            acc[key] = value;
          }
          return acc;
        }, {});

        const logsResponse = await getCreditUsageLogs(cleanFilters);
        
        // Validate response structure
        if (!logsResponse?.data) {
          throw new Error('Invalid response format from server');
        }

        const responseData = logsResponse.data.data || [];
        const responsePagination = logsResponse.data.pagination || {};
        
        setLogs(responseData);
        setPagination(responsePagination);
      } else if (activeTab === 'invoices') {
        const purchaseResponse = await getPurchaseEntries(teamUuid);
        
        // Validate response structure
        if (!purchaseResponse?.data) {
          throw new Error('Invalid response format from server');
        }

        const purchaseData = purchaseResponse.data.purchase_entries || [];
        setPurchaseEntries(purchaseData);
      }
      
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setError(error.message || 'Failed to load billing data. Please try again.');
      if (activeTab === 'usage') {
        setLogs([]);
        setPagination({});
      } else {
        setPurchaseEntries([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatOperationType = (type) => {
    try {
      if (!type) return 'Unknown Operation';
      return t(`Billing.Operations.${type}`, type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    } catch (error) {
      console.error('Error formatting operation type:', error);
      return 'Unknown Operation';
    }
  };

  const translateDescription = (description, operationType) => {
    try {
      if (!operationType) return description || 'No description available';
      
      // Check if we have a specific translation for this description
      const translationKey = `Billing.Descriptions.${operationType}`;
      const translated = t(translationKey, { defaultValue: null });
      
      // If we have a translation, use it; otherwise, return the original description
      return translated || description || 'No description available';
    } catch (error) {
      console.error('Error translating description:', error);
      return description || 'No description available';
    }
  };

  const formatAmount = (amount) => {
    try {
      if (amount === undefined || amount === null) return '0.00';
      
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return '0.00';
      
      return numAmount.toFixed(2);
    } catch (error) {
      console.error('Error formatting amount:', error);
      return '0.00';
    }
  };

  const formatPrice = (originalPrice, originalCurrency) => {
    try {
      if (originalPrice === undefined || originalPrice === null) return 'N/A';
      if (!originalCurrency) return 'N/A';
      
      const numPrice = parseFloat(originalPrice);
      if (isNaN(numPrice)) return 'N/A';
      
      // Format currency symbol based on currency code
      const getCurrencySymbol = (currency) => {
        switch (currency?.toUpperCase()) {
          case 'USD': return '$';
          case 'EUR': return '€';
          case 'BRL': return 'R$';
          case 'GBP': return '£';
          default: return currency + ' ';
        }
      };
      
      const symbol = getCurrencySymbol(originalCurrency);
      return `${symbol}${numPrice.toFixed(2)}`;
    } catch (error) {
      console.error('Error formatting price:', error);
      return 'N/A';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOperationIcon = (type) => {
    switch (type) {
      case 'meeting_transcription':
      case 'meeting_question':
        return <CalendarIcon className="h-5 w-5" />;
      case 'ai_code_analysis':
        return <CogIcon className="h-5 w-5" />;
      case 'transcription_question':
        return <UserIcon className="h-5 w-5" />;
      default:
        return <CpuChipIcon className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 md:pt-12 w-full flex justify-center`}>
        <div className='flex flex-col w-full justify-center items-center gap-4 px-4'>
          <div className='bg-white max-w-7xl rounded-2xl w-full'>
            <SectionHeader title={t('Billing.Title')} subtitle={t('Billing.Subtitle')} />
          </div>
          <Card className='w-full max-w-7xl'>
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-iavisus-100"></div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 md:pt-12 w-full flex justify-center`}>
        <div className='flex flex-col w-full justify-center items-center gap-4 px-4'>
          <div className='bg-white max-w-7xl rounded-2xl w-full'>
            <SectionHeader title={t('Billing.Title')} subtitle={t('Billing.Subtitle')} />
          </div>
          <Card className='w-full max-w-7xl'>
            <div className="flex flex-col items-center justify-center h-64 text-center px-6">
              <CpuChipIcon className="h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-sm text-gray-500 mb-4">{error}</p>
              <button
                onClick={() => fetchData()}
                className="px-4 py-2 bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 md:pt-12 w-full flex justify-center`}>
      <div className='flex flex-col w-full justify-center items-center gap-4 px-4'>
        <div className='bg-white max-w-7xl rounded-2xl w-full'>
          <SectionHeader title={t('Billing.Title')} subtitle={t('Billing.Subtitle')} />
        </div>
        <Card className='w-full max-w-7xl'>
          {/* Tab Navigation */}
          <div className="py-4 border-b border-gray-200">
            {/* Mobile Tab Selector */}
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                Select a tab
              </label>
              <select
                id="tabs"
                name="tabs"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="block w-full rounded-md border-gray-300 focus:border-iavisus-100 focus:ring-iavisus-100"
              >
                <option value="usage">{t('Billing.UsageTab')}</option>
                <option value="invoices">{t('Billing.InvoicesTab')}</option>
              </select>
            </div>
            
            {/* Desktop Tab Navigation */}
            <div className="hidden sm:block">
              <nav className="flex gap-2" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('usage')}
                  className={`rounded-md px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === 'usage'
                      ? 'bg-iavisus-100 text-white'
                      : 'border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-100 hover:text-white'
                  }`}
                  aria-current={activeTab === 'usage' ? 'page' : undefined}
                >
                  {t('Billing.UsageTab')}
                </button>
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`rounded-md px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === 'invoices'
                      ? 'bg-iavisus-100 text-white'
                      : 'border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-100 hover:text-white'
                  }`}
                  aria-current={activeTab === 'invoices' ? 'page' : undefined}
                >
                  {t('Billing.InvoicesTab')}
                </button>
              </nav>
            </div>
          </div>

          {/* Usage Table */}
          {activeTab === 'usage' && (
            <div className="bg-white rounded-lg border border-gray-200 mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('Billing.LogsTitle')}</h3>
              </div>

            {/* Logs Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Billing.Date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Billing.Operation')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Billing.Description')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Billing.Amount')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Billing.BalanceAfter')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <CpuChipIcon className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-sm font-medium text-gray-900 mb-2">{t('Billing.NoDataTitle')}</h3>
                          <p className="text-sm text-gray-500">{t('Billing.NoDataSubtitle')}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logs.map((log, index) => {
                      // Ensure log exists and has required properties
                      if (!log) return null;
                      
                      const safeLog = {
                        operation_type: log.operation_type || 'unknown',
                        created_at: log.created_at || null,
                        description: log.description || '',
                        credits_amount: log.credits_amount || '0',
                        balance_after: log.balance_after || '0'
                      };

                      return (
                        <tr key={`${safeLog.operation_type}-${safeLog.created_at}-${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(safeLog.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 text-gray-400">
                                {getOperationIcon(safeLog.operation_type)}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {formatOperationType(safeLog.operation_type)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                            {translateDescription(safeLog.description, safeLog.operation_type)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              parseFloat(safeLog.credits_amount) < 0 
                                ? 'bg-gray-100 text-gray-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {parseFloat(safeLog.credits_amount) < 0 ? '' : '+'}
                              {formatAmount(safeLog.credits_amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatAmount(safeLog.balance_after)}
                          </td>
                        </tr>
                      );
                    }).filter(Boolean)
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && logs.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t('Billing.ShowingResults', {
                      from: pagination.from || 1,
                      to: pagination.to || logs.length,
                      total: pagination.total || logs.length
                    })}
                  </div>
                  {pagination.total > (pagination.per_page || 15) && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        className="px-3 py-2 border border-iavisus-100 rounded-md text-sm font-medium text-iavisus-100 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('Billing.Previous')}
                      </button>
                      <span className="px-3 py-2 text-sm text-gray-700">
                        {t('Billing.Page', {
                          current: pagination.current_page,
                          last: pagination.last_page
                        })}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        className="px-3 py-2 border border-iavisus-100 rounded-md text-sm font-medium text-iavisus-100 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('Billing.Next')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Invoices Table */}
          {activeTab === 'invoices' && (
            <div className="bg-white rounded-lg border border-gray-200 mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('Billing.InvoicesTitle')}</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Billing.Date')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Billing.Amount')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Billing.Price')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Billing.Type')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Billing.StatusHeader')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Billing.Receipt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {purchaseEntries.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <DocumentTextIcon className="h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-2">{t('Billing.NoInvoicesTitle')}</h3>
                            <p className="text-sm text-gray-500">{t('Billing.NoInvoicesSubtitle')}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      purchaseEntries.map((entry, index) => {
                        if (!entry) return null;
                        
                        const safeEntry = {
                          created_at: entry.created_at || null,
                          credits_amount: entry.amount || entry.credits_amount || '0',
                          original_price: entry.original_price || null,
                          original_currency: entry.original_currency || null,
                          type: entry.type || 'purchase',
                          status: entry.status || 'unknown',
                          receipt_url: entry.receipt_url || null
                        };

                        return (
                          <tr key={`${safeEntry.created_at}-${index}`} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(safeEntry.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatAmount(safeEntry.credits_amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatPrice(safeEntry.original_price, safeEntry.original_currency)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {t(`Billing.PurchaseTypes.${safeEntry.type}`, safeEntry.type)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(safeEntry.status)}`}>
                                {t(`Billing.StatusValues.${safeEntry.status}`, safeEntry.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {safeEntry.receipt_url ? (
                                <a
                                  href={safeEntry.receipt_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1 border border-iavisus-100 rounded-md text-sm font-medium text-iavisus-100 bg-white hover:bg-gray-50"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                                  {t('Billing.Download')}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">{t('Billing.NoReceipt')}</span>
                              )}
                            </td>
                          </tr>
                        );
                      }).filter(Boolean)
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default BillingUsage;